<template>
  <AppLayout>
    <div class="mb-6 flex items-center gap-4 flex-wrap">
      <Button
        v-tooltip.bottom="'List view options'"
        variant="text"
        severity="secondary"
        icon="pi pi-sliders-h"
        rounded
        aria-label="List view options"
        @click="adjustPopover.toggle($event)"
      />

      <Button
        v-tooltip.bottom="attentionFilter ? 'Show all' : 'Show attention'"
        icon="pi pi-exclamation-triangle"
        variant="text"
        rounded
        :severity="attentionFilter ? 'danger' : 'secondary'"
        aria-label="Attention only"
        @click="attentionFilter = !attentionFilter"
      />

      <Button
        v-if="auth.user.value?.superAdmin"
        v-tooltip.bottom="pendingFilter ? 'Show all' : 'Show pending'"
        icon="pi pi-hourglass"
        variant="text"
        rounded
        :severity="pendingFilter ? 'info' : 'secondary'"
        aria-label="Pending only"
        @click="pendingFilter = !pendingFilter"
      />

      <Button
        v-if="auth.user.value?.superAdmin"
        v-tooltip.bottom="allOrgs ? 'Showing all organizations' : 'Show all organizations'"
        icon="pi pi-globe"
        variant="text"
        rounded
        :severity="allOrgs ? 'info' : 'secondary'"
        aria-label="All organizations"
        @click="allOrgs = !allOrgs"
      />

      <InputText v-model="searchInput" placeholder="Search by code or name" class="w-64" />

      <InputText v-if="allOrgs" v-model="userEmailInput" placeholder="User email" class="w-64" />

      <Button
        v-if="allOrgs && orgFilterId"
        v-tooltip.bottom="'Clear organization filter'"
        icon="pi pi-times"
        :label="`Org: ${orgFilterName ?? orgFilterId}`"
        severity="info"
        variant="text"
        rounded
        aria-label="Clear organization filter"
        @click="setQuery({ orgId: undefined })"
      />

      <Select
        v-model="datePreset"
        :options="datePresetOptions"
        optionLabel="label"
        optionValue="value"
        class="w-40"
      />
      <DatePicker
        v-if="datePreset === 'date'"
        v-model="customDate"
        placeholder="Date"
        dateFormat="yy-mm-dd"
        showIcon
        showButtonBar
        class="w-40"
      />
      <template v-if="datePreset === 'custom'">
        <DatePicker
          v-model="customFromDate"
          placeholder="From"
          dateFormat="yy-mm-dd"
          showIcon
          showButtonBar
          class="w-40"
        />
        <DatePicker
          v-model="customToDate"
          placeholder="To"
          dateFormat="yy-mm-dd"
          showIcon
          showButtonBar
          class="w-40"
        />
      </template>

      <div class="ml-auto flex items-center">
        <Button
          variant="text"
          severity="secondary"
          icon="pi pi-ellipsis-v"
          rounded
          aria-label="More actions"
          @click="actionsMenu?.toggle($event)"
        />
        <Menu ref="actionsMenu" :model="actionsMenuItems" :popup="true" />
      </div>
    </div>

    <Popover ref="adjustPopover" class="product-list-adjust-popover">
      <div class="flex flex-col gap-4 min-w-56">
        <div class="hidden md:block">
          <label class="text-sm font-medium text-surface-700 dark:text-surface-300 block mb-2">
            Image size
          </label>
          <Slider v-model="listImageSize" :min="50" :max="200" :step="5" class="w-full" />
          <div class="text-xs text-surface-500 mt-1">{{ listImageSize }}%</div>
        </div>
        <div>
          <label class="text-sm font-medium text-surface-700 dark:text-surface-300 block mb-2">
            View
          </label>
          <SelectButton
            v-model="viewType"
            :options="viewTypeOptions"
            optionLabel="label"
            optionValue="value"
          />
        </div>
        <div class="flex items-center justify-between gap-2">
          <label
            for="group-by-date-toggle"
            class="text-sm font-medium text-surface-700 dark:text-surface-300"
          >
            Group by date
          </label>
          <ToggleSwitch id="group-by-date-toggle" v-model="groupByDate" />
        </div>
        <div
          v-if="auth.user.value?.superAdmin"
          class="flex items-center justify-between gap-2"
        >
          <label
            for="show-user-avatars-toggle"
            class="text-sm font-medium text-surface-700 dark:text-surface-300"
          >
            Show user avatars
          </label>
          <ToggleSwitch id="show-user-avatars-toggle" v-model="showUserAvatars" />
        </div>
      </div>
    </Popover>

    <Paginator
      :first="currentOffset"
      :rows="currentLimit"
      :totalRecords="totalRecords"
      :rowsPerPageOptions="[50, 100, 200]"
      @page="onPage"
      class="mb-4"
    />

    <div v-if="loading" class="flex justify-center p-8">
      <ProgressSpinner style="width: 50px; height: 50px" />
    </div>

    <template v-else v-for="section in displaySections" :key="section.date ?? 'all'">
      <h2 v-if="section.date" class="mb-4">{{ section.date }}</h2>

      <div v-if="section.products.length === 0" class="text-surface-500 mb-4">(No products)</div>

      <!-- Grid view -->
      <div
        v-else-if="viewType === 'grid'"
        class="flex flex-row flex-wrap gap-2 mb-4 product-list-grid"
        :style="{
          '--list-image-size': `${listImageSizePx}px`,
        }"
      >
        <div
          v-for="product in section.products"
          :key="product.id"
          class="product-card-wrapper group list-card"
        >
          <div class="overflow-hidden">
            <RouterLink :to="`/products/${product.id}`" class="no-underline block">
              <div
                class="aspect-square overflow-hidden rounded-lg p-2 md:p-3 flex items-center justify-center box-border relative"
                :class="
                  product.hasAttention || product.hasFailedTasks
                    ? 'bg-red-500/40'
                    : product.hasPendingTasks
                      ? 'bg-blue-500/40'
                      : 'bg-surface-800'
                "
              >
                <img
                  :src="getImageUrl(product.imageUrl, product.imageContentType, listImageSizePx)"
                  loading="lazy"
                  class="max-w-full max-h-full w-auto h-auto object-contain align-middle"
                />
                <UserAvatar
                  v-if="showUserAvatars && auth.user.value?.superAdmin && product.userEmail"
                  :email="product.userEmail"
                  :display-name="product.userDisplayName"
                  :org-name="product.orgName"
                  clickable
                  class="absolute top-1 right-1"
                  @click.stop.prevent="userEmailInput = product.userEmail ?? ''"
                />
              </div>
            </RouterLink>
            <div class="px-2 md:pt-1.5 flex items-center justify-between gap-1 min-w-0">
              <span
                class="flex items-center gap-1 text-xs text-surface-500 dark:text-surface-400 shrink-0"
              >
                <i class="pi pi-images" />
                {{ product.imageCount }}
              </span>
              <div class="flex items-center shrink-0">
                <Button
                  @click.prevent="confirmDeleteProduct(product)"
                  icon="pi pi-trash"
                  text
                  rounded
                  size="small"
                  class="transition-opacity opacity-0 group-hover:opacity-100 text-surface-400 dark:text-surface-500"
                  severity="secondary"
                />
                <Button
                  @click.prevent="openWorkflowDialog(product)"
                  icon="pi pi-sitemap"
                  text
                  rounded
                  size="small"
                  class="transition-opacity"
                  :class="{
                    'text-red-500': product.hasFailedTasks,
                    'text-blue-500':
                      !product.hasFailedTasks && product.hasPendingTasks,
                    'text-surface-400 dark:text-surface-500 opacity-0 group-hover:opacity-100':
                      !product.hasFailedTasks && !product.hasPendingTasks,
                  }"
                  :severity="
                    product.hasFailedTasks
                      ? 'danger'
                      : product.hasPendingTasks
                        ? 'info'
                        : 'secondary'
                  "
                />
                <Button
                  @click.prevent="toggleAttention(product)"
                  icon="pi pi-exclamation-triangle"
                  text
                  rounded
                  size="small"
                  class="transition-opacity"
                  :class="{
                    'text-red-500': product.hasAttention,
                    'text-surface-400 dark:text-surface-500 opacity-0 group-hover:opacity-100':
                      !product.hasAttention,
                  }"
                  :severity="product.hasAttention ? 'danger' : 'secondary'"
                />
              </div>
            </div>
            <RouterLink :to="`/products/${product.id}`" class="no-underline block px-2 pb-2">
              <div
                class="text-sm text-surface-400 dark:text-surface-0 truncate"
                :title="product.name || product.code || ''"
              >
                {{ product.name || product.code || '\u00A0' }}
              </div>
              <div
                v-if="allOrgs && product.orgName"
                class="text-xs text-surface-500 truncate"
                :title="`${product.orgName} (${product.userEmail ?? ''})`"
              >
                {{ product.orgName }}
              </div>
            </RouterLink>
          </div>
        </div>
      </div>

      <!-- Table view -->
      <div v-else class="mb-4 overflow-x-auto">
        <table class="w-full text-sm border-collapse">
          <thead>
            <tr class="border-b border-surface-200 dark:border-surface-700">
              <th
                class="text-left py-2 pr-2 font-medium text-surface-600 dark:text-surface-400 w-0"
              >
                Image
              </th>
              <th class="text-left py-2 pr-2 font-medium text-surface-600 dark:text-surface-400">
                Name
              </th>
              <th
                v-if="allOrgs"
                class="text-left py-2 pr-2 font-medium text-surface-600 dark:text-surface-400"
              >
                User
              </th>
              <th class="text-left py-2 pr-2 font-medium text-surface-600 dark:text-surface-400">
                Photos
              </th>
              <th class="w-10 py-2"></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="product in section.products"
              :key="product.id"
              class="border-b border-surface-100 dark:border-surface-800 hover:bg-surface-50 dark:hover:bg-surface-800/50"
              :class="{
                'bg-red-50/50 dark:bg-red-950/20 border-l-2 border-l-red-400/60':
                  product.hasAttention || product.hasFailedTasks,
                'bg-blue-50/50 dark:bg-blue-950/20 border-l-2 border-l-blue-400/60':
                  !product.hasAttention &&
                  !product.hasFailedTasks &&
                  product.hasPendingTasks,
              }"
            >
              <td class="py-2 pr-2 align-middle">
                <RouterLink :to="`/products/${product.id}`" class="block w-fit">
                  <div
                    class="overflow-hidden rounded-lg list-card-image aspect-square shrink-0 relative"
                    :style="{ width: `${listImageSizePx}px`, height: `${listImageSizePx}px` }"
                  >
                    <img
                      :src="
                        getImageUrl(product.imageUrl, product.imageContentType, listImageSizePx)
                      "
                      loading="lazy"
                      class="w-full h-full object-contain rounded-lg"
                    />
                    <UserAvatar
                      v-if="showUserAvatars && auth.user.value?.superAdmin && product.userEmail"
                      :email="product.userEmail"
                      :display-name="product.userDisplayName"
                      :org-name="product.orgName"
                      clickable
                      class="absolute top-1 right-1"
                      @click.stop.prevent="userEmailInput = product.userEmail ?? ''"
                    />
                  </div>
                </RouterLink>
              </td>
              <td class="py-2 pr-2 align-middle">
                <RouterLink
                  :to="`/products/${product.id}`"
                  class="text-surface-900 dark:text-surface-0 hover:underline no-underline"
                >
                  {{ product.name || product.code || '—' }}
                </RouterLink>
              </td>
              <td v-if="allOrgs" class="py-2 pr-2 align-middle">
                <div class="flex flex-col min-w-0">
                  <button
                    type="button"
                    class="text-sm text-surface-900 dark:text-surface-0 hover:underline text-left bg-transparent border-0 p-0 cursor-pointer truncate"
                    @click="userEmailInput = product.userEmail ?? ''"
                  >
                    {{ product.userEmail }}
                  </button>
                  <span class="text-xs text-surface-500 truncate">{{ product.orgName }}</span>
                </div>
              </td>
              <td class="py-2 pr-2 align-middle text-surface-500 dark:text-surface-400">
                <span class="flex items-center gap-1">
                  <i class="pi pi-images" />
                  {{ product.imageCount }}
                </span>
              </td>
              <td class="py-2 align-middle">
                <div class="flex items-center">
                  <Button
                    @click.prevent="openWorkflowDialog(product)"
                    icon="pi pi-sitemap"
                    text
                    rounded
                    size="small"
                    :class="{
                      'text-red-500': product.hasFailedTasks,
                      'text-blue-500':
                        !product.hasFailedTasks &&
                        product.hasPendingTasks,
                      'text-surface-400 dark:text-surface-500':
                        !product.hasFailedTasks &&
                        !product.hasPendingTasks,
                    }"
                    :severity="
                      product.hasFailedTasks
                        ? 'danger'
                        : product.hasPendingTasks
                          ? 'info'
                          : 'secondary'
                    "
                  />
                  <Button
                    @click.prevent="toggleAttention(product)"
                    icon="pi pi-exclamation-triangle"
                    text
                    rounded
                    size="small"
                    :class="{
                      'text-red-500': product.hasAttention,
                      'text-surface-400 dark:text-surface-500': !product.hasAttention,
                    }"
                    :severity="product.hasAttention ? 'danger' : 'secondary'"
                  />
                  <Button
                    @click.prevent="confirmDeleteProduct(product)"
                    icon="pi pi-trash"
                    text
                    rounded
                    size="small"
                    class="text-surface-400 dark:text-surface-500"
                    severity="secondary"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <Paginator
      :first="currentOffset"
      :rows="currentLimit"
      :totalRecords="totalRecords"
      :rowsPerPageOptions="[50, 100, 200]"
      @page="onPage"
      class="my-4"
    />

    <UploadImagesDialog v-model:visible="uploadFixedImagesDialogVisible" />
    <ProductWorkflowDialog
      v-if="workflowDialogProductId"
      v-model:visible="workflowDialogVisible"
      :product-id="workflowDialogProductId"
    />
  </AppLayout>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';
import AppLayout from '@/components/AppLayout.vue';
import dayjs from 'dayjs';
import Button from 'primevue/button';
import DatePicker from 'primevue/datepicker';
import InputText from 'primevue/inputtext';
import Menu from 'primevue/menu';
import type { MenuItem } from 'primevue/menuitem';
import Paginator from 'primevue/paginator';
import Popover from 'primevue/popover';
import ProgressSpinner from 'primevue/progressspinner';
import Select from 'primevue/select';
import SelectButton from 'primevue/selectbutton';
import Slider from 'primevue/slider';
import ToggleSwitch from 'primevue/toggleswitch';
import { useConfirm } from 'primevue/useconfirm';
import { useDebounceFn } from '@vueuse/core';
import type { ListProductsEntryDto, OrganizationDto } from '@/api/generated';
import { groupBy } from 'lodash';
import { useApiClient } from '@/api/apiClient';
import { useAuth } from '@/modules/auth/auth';
import UploadImagesDialog from '@/modules/products/upload-images-dialog/UploadImagesDialog.vue';
import ProductWorkflowDialog from '@/modules/products/workflow/ProductWorkflowDialog.vue';
import UserAvatar from '@/components/UserAvatar.vue';

const { api } = useApiClient();
const auth = useAuth();
const confirm = useConfirm();
const route = useRoute();
const router = useRouter();

const loading = ref(true);
const error = ref('');
const products = ref<ListProductsEntryDto[]>([]);

// By default any filter change resets pagination; onPage opts out with keepOffset.
function setQuery(
  patch: Record<string, string | undefined>,
  opts: { keepOffset?: boolean } = {},
) {
  const next: Record<string, string> = { ...(route.query as Record<string, string>) };
  if (!opts.keepOffset) delete next.offset;
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined || v === '') delete next[k];
    else next[k] = v;
  }
  router.replace({ query: next });
}

function queryString(key: string): string {
  const v = route.query[key];
  return typeof v === 'string' ? v : '';
}

function queryBool(key: string): boolean {
  return route.query[key] === 'true';
}

function parseDate(v: string): Date | null {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

const attentionFilter = computed({
  get: () => queryBool('attention'),
  set: v => setQuery({ attention: v ? 'true' : undefined }),
});
const pendingFilter = computed({
  get: () => queryBool('pending'),
  set: v => setQuery({ pending: v ? 'true' : undefined }),
});
const datePreset = computed({
  get: () => queryString('datePreset') || 'last-7-days',
  set: v => setQuery({ datePreset: v === 'last-7-days' ? undefined : v }),
});
const customDate = computed({
  get: () => parseDate(queryString('customDate')),
  set: v => setQuery({ customDate: v ? dayjs(v).format('YYYY-MM-DD') : undefined }),
});
const customFromDate = computed({
  get: () => parseDate(queryString('customFromDate')),
  set: v => setQuery({ customFromDate: v ? dayjs(v).format('YYYY-MM-DD') : undefined }),
});
const customToDate = computed({
  get: () => parseDate(queryString('customToDate')),
  set: v => setQuery({ customToDate: v ? dayjs(v).format('YYYY-MM-DD') : undefined }),
});

// Local ref for the search input; debounced write to URL to avoid churn per keystroke
const searchInput = ref(queryString('search'));
const writeSearch = useDebounceFn(
  (val: string) => setQuery({ search: val.trim() || undefined }),
  300,
);
watch(searchInput, val => writeSearch(val));
watch(
  () => queryString('search'),
  v => {
    if (v !== searchInput.value) searchInput.value = v;
  },
);

// Super-admin "All organizations" toggle: gates the admin endpoint + user-email filter.
// AND-gated on superAdmin so a shared URL with allOrgs=true read by a regular user is a no-op.
const allOrgs = computed({
  get: () => Boolean(auth.user.value?.superAdmin) && queryBool('allOrgs'),
  set: v =>
    setQuery({
      allOrgs: v ? 'true' : undefined,
      userEmail: v ? queryString('userEmail') || undefined : undefined,
      orgId: v ? queryString('orgId') || undefined : undefined,
    }),
});

// Organization scope arrives only via URL (deep links from HQ) — no input
// field; the chip next to the filters names the org and clears the scope.
const orgFilterId = computed(() => queryString('orgId'));
const orgFilterName = ref<string | null>(null);
// The admin org list is fetched once per page lifetime, lazily on the first
// org-scoped deep link, and reused for later orgId changes.
let adminOrgsPromise: Promise<OrganizationDto[] | null> | null = null;
watch(
  () => (allOrgs.value ? queryString('orgId') : ''),
  async orgId => {
    orgFilterName.value = null;
    if (!orgId) return;
    adminOrgsPromise ??= api
      .adminOrganizationControllerList()
      .then(r => r.data ?? null)
      .catch(() => null);
    const orgs = await adminOrgsPromise;
    if (!orgs) adminOrgsPromise = null; // failed fetch — retry on next change
    if (queryString('orgId') !== orgId) return; // filter changed while loading
    orgFilterName.value = orgs?.find(org => org.id === orgId)?.name ?? null;
  },
  { immediate: true },
);

const userEmailInput = ref(queryString('userEmail'));
const writeUserEmail = useDebounceFn(
  (val: string) => setQuery({ userEmail: val.trim() || undefined }),
  300,
);
watch(userEmailInput, val => writeUserEmail(val));
watch(
  () => queryString('userEmail'),
  v => {
    if (v !== userEmailInput.value) userEmailInput.value = v;
  },
);

// Normalise URL: strip admin-only params for non-super-admins; auto-enable allOrgs
// when a super-admin lands with userEmail or orgId set (deep-link from
// ProductDetailPage / HQ).
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
watch(
  () =>
    [
      auth.user.value?.superAdmin,
      queryBool('allOrgs'),
      queryString('userEmail'),
      queryString('orgId'),
    ] as const,
  ([isSuper, hasAllOrgs, userEmail, orgId]) => {
    if (!isSuper && (hasAllOrgs || userEmail || orgId)) {
      setQuery({ allOrgs: undefined, userEmail: undefined, orgId: undefined });
    } else if (isSuper && orgId && !UUID_RE.test(orgId)) {
      // A malformed orgId (hand-edited link, org code instead of id) would be
      // 400-rejected and blank the whole list — drop the param instead.
      setQuery({ orgId: undefined });
    } else if (isSuper && (userEmail || orgId) && !hasAllOrgs) {
      setQuery({ allOrgs: 'true' });
    }
  },
  { immediate: true },
);

// Default ON; only off if user has explicitly disabled it
const showUserAvatars = ref(localStorage.getItem('productListShowUserAvatars') !== 'false');
watch(showUserAvatars, val => localStorage.setItem('productListShowUserAvatars', String(val)));

// Pagination lives in URL so back-nav and refresh preserve the page.
const currentOffset = computed(() => Number(queryString('offset')) || 0);
const currentLimit = computed(() => Number(queryString('limit')) || 50);
const totalRecords = ref(0);

// Date filter
const datePresetOptions = [
  { label: 'Today', value: 'today' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'Last 7 days', value: 'last-7-days' },
  { label: 'Last 30 days', value: 'last-30-days' },
  { label: 'All time', value: 'all' },
  { label: 'Date', value: 'date' },
  { label: 'Date range', value: 'custom' },
];

const dateRange = computed(() => {
  const now = dayjs();
  switch (datePreset.value) {
    case 'today':
      return { from: now.startOf('day').toISOString(), to: now.endOf('day').toISOString() };
    case 'yesterday': {
      const yesterday = now.subtract(1, 'day');
      return { from: yesterday.startOf('day').toISOString(), to: yesterday.endOf('day').toISOString() };
    }
    case 'last-7-days':
      return { from: now.subtract(7, 'day').startOf('day').toISOString(), to: now.endOf('day').toISOString() };
    case 'last-30-days':
      return { from: now.subtract(30, 'day').startOf('day').toISOString(), to: now.endOf('day').toISOString() };
    case 'date':
      return {
        from: customDate.value ? dayjs(customDate.value).startOf('day').toISOString() : undefined,
        to: customDate.value ? dayjs(customDate.value).endOf('day').toISOString() : undefined,
      };
    case 'custom':
      return {
        from: customFromDate.value ? dayjs(customFromDate.value).startOf('day').toISOString() : undefined,
        to: customToDate.value ? dayjs(customToDate.value).endOf('day').toISOString() : undefined,
      };
    default:
      return { from: undefined, to: undefined };
  }
});

const uploadFixedImagesDialogVisible = ref(false);
const workflowDialogProductId = ref<string | null>(null);
const workflowDialogVisible = ref(false);

function openWorkflowDialog(product: ListProductsEntryDto) {
  workflowDialogProductId.value = product.id;
  workflowDialogVisible.value = true;
}

const adjustPopover = ref();
const actionsMenu = ref<InstanceType<typeof Menu>>();

const actionsMenuItems = computed((): MenuItem[] => [
  ...(auth.user.value?.superAdmin
    ? [
        {
          label: 'Download Problematic Images',
          icon: 'pi pi-download',
          command: () => {
            const { from, to } = dateRange.value;
            const qs = new URLSearchParams();
            if (from) qs.set('from', from);
            if (to) qs.set('to', to);
            const s = qs.toString();
            window.open(
              `/api/images/download-problematic-images${s ? `?${s}` : ''}`,
              '_blank',
              'noopener,noreferrer',
            );
          },
        },
      ]
    : []),
  {
    label: 'Upload Fixed Images',
    icon: 'pi pi-upload',
    command: () => (uploadFixedImagesDialogVisible.value = true),
  },
]);

const listImageSize = ref(Number(localStorage.getItem('productListImageSize')) || 100); // 100% = 150px, range 50–200%
watch(listImageSize, val => localStorage.setItem('productListImageSize', String(val)));
const listImageSizePx = computed(() => Math.round((listImageSize.value / 100) * 230));
const viewTypeOptions = [
  { label: 'Grid', value: 'grid' },
  { label: 'Table', value: 'table' },
];
const viewType = ref<'grid' | 'table'>('grid');
const groupByDate = ref(true);

// Any change to filter/pagination state in the URL triggers a reload.
// Filter setters reset offset via setQuery; onPage writes offset/limit directly.
watch(
  () => [
    queryBool('attention'),
    queryBool('pending'),
    queryBool('allOrgs'),
    queryString('userEmail'),
    queryString('orgId'),
    queryString('search'),
    queryString('datePreset'),
    queryString('customDate'),
    queryString('customFromDate'),
    queryString('customToDate'),
    currentOffset.value,
    currentLimit.value,
  ],
  () => loadProducts(),
);

function onPage(event: { first: number; rows: number }) {
  setQuery(
    {
      offset: event.first > 0 ? String(event.first) : undefined,
      limit: event.rows !== 50 ? String(event.rows) : undefined,
    },
    { keepOffset: true },
  );
}

let inflightController: AbortController | null = null;

async function loadProducts() {
  inflightController?.abort();
  const controller = new AbortController();
  inflightController = controller;
  try {
    loading.value = true;
    const { from, to } = dateRange.value;
    const baseBody = {
      offset: currentOffset.value,
      limit: currentLimit.value,
      attention: attentionFilter.value,
      pending: pendingFilter.value,
      from,
      to,
      search: queryString('search').trim() || undefined,
    };
    const { data, error: err } = allOrgs.value
      ? await api.adminProductControllerList({
          body: {
            ...baseBody,
            userEmail: queryString('userEmail').trim() || undefined,
            orgId: queryString('orgId').trim() || undefined,
          },
          signal: controller.signal,
        })
      : await api.productControllerList({
          body: baseBody,
          signal: controller.signal,
        });
    if (controller.signal.aborted) return;
    if (err) {
      throw new Error('Failed to load products');
    }
    products.value = data?.data ?? [];
    totalRecords.value = data?.total ?? 0;
  } catch (err) {
    if (controller.signal.aborted) return;
    error.value = 'Failed to load products';
    throw err;
  } finally {
    if (!controller.signal.aborted) {
      loading.value = false;
    }
  }
}

const productsByDay = computed(() => {
  const res = groupBy(products.value, p => dayjs(p.created).format('YYYY-MM-DD'));
  const entries = Object.entries(res).map(([date, products]) => ({
    date,
    products,
  }));
  return entries;
});

const displaySections = computed(() => {
  if (groupByDate.value) {
    return productsByDay.value;
  }
  return [{ date: null as string | null, products: products.value }];
});

// On a deep link (?userEmail= / ?orgId= without allOrgs) the normalise watch
// is about to rewrite the URL and the query watch will fire the right admin
// request once it lands — skip the setup-time load so we don't also hit the
// non-admin endpoint with the filter dropped.
const deepLinkPending =
  Boolean(auth.user.value?.superAdmin) &&
  !queryBool('allOrgs') &&
  Boolean(queryString('userEmail') || queryString('orgId'));
if (!deepLinkPending) loadProducts();

async function toggleAttention(product: ListProductsEntryDto) {
  const attention = !product.hasAttention;
  const { error } = await api.productControllerSetAttention({
    path: {
      productId: product.id,
    },
    body: {
      attention,
    },
  });
  if (error) {
    throw new Error('Failed to set attention');
  }
  product.hasAttention = attention;
}

function confirmDeleteProduct(product: ListProductsEntryDto) {
  confirm.require({
    message: `Are you sure you want to delete "${product.name || product.code}"?`,
    header: 'Delete Product',
    icon: 'pi pi-trash',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    acceptClass: 'p-button-danger',
    accept: () => deleteProduct(product),
  });
}

async function deleteProduct(product: ListProductsEntryDto) {
  const { error } = await api.productControllerDelete({
    path: {
      productId: product.id,
    },
  });
  if (error) {
    throw new Error('Failed to delete product');
  }
  products.value = products.value.filter(p => p.id !== product.id);
}

function getImageUrl(imageUrl: string, contentType: string, width: number): string | undefined {
  const widthPx = devicePixelRatio * width;
  let url = `${imageUrl}=s${widthPx}`;
  if (contentType === 'image/webp') {
    url += '-rw';
  }
  return url;
}

// async function onProductCreated() {
//   // TODO: how to reset dialog state?
//   reset.value = true;
//   nextTick(() => {
//     reset.value = false;
//   });
// }
</script>

<style scoped>
.product-list-grid .list-card {
  width: var(--list-image-size);
  min-width: var(--list-image-size);
}

/* Mobile: at least 3 per row; respect image size slider (smaller size = more columns) */
@media (max-width: 767px) {
  .product-list-grid .list-card {
    width: min(var(--list-image-size), calc((100% - 1rem) / 3));
    min-width: min(var(--list-image-size), calc((100% - 1rem) / 3));
  }
}

</style>
